import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { useMemo, useState } from 'react';
import { cn } from '../../lib/cn';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { Button } from './Button';
import { Spinner } from './Spinner';

export const DEFAULT_TABLE_PAGE_SIZE = 25;

/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- TanStack's ColumnDef value type is invariant; `any` is the documented way to hold a heterogeneous column list. */
export type DataTableColumns<TData> = ColumnDef<TData, any>[];

/** The paged list the server answered with, and the page the footer asks for next. */
export interface DataTablePagination {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

/** The sort the server applied, and the one a header click asks for next. */
export interface DataTableSorting {
  sortBy: string;
  sortDirection: 'asc' | 'desc';
  onSortChange: (sortBy: string, direction: 'asc' | 'desc') => void;
}

export interface DataTableProps<TData> {
  columns: DataTableColumns<TData>;
  data: TData[];
  isLoading?: boolean;
  emptyMessage?: string;
  onRowClick?: (row: TData) => void;
  pageSize?: number;
  /** Given: the rows are one server page, and paging is the caller's to perform (plan §11 fix 12). */
  pagination?: DataTablePagination;
  /** Given: the rows arrive sorted, and a header click asks the server for a different order. */
  sorting?: DataTableSorting;
  className?: string;
}

/**
 * Thin, typed wrapper over `@tanstack/react-table`: sortable headers, page pagination, loading and
 * empty states, optional row click through to a detail route.
 *
 * It works two ways. Without `pagination`/`sorting` it holds the whole list and pages and sorts it in
 * the browser, which is right for the short in-memory tables (a template's versions, a policy's
 * versions, the dashboard's recent rows). With them it is a **view of one server page**: sorting and
 * paging the 25 rows a query returned can only ever reorder those 25, so the long lists hand both back
 * to the service and keep their position in the URL (plan §11 fix 12).
 */
export const DataTable = <TData,>({
  columns,
  data,
  isLoading = false,
  emptyMessage,
  onRowClick,
  pageSize = DEFAULT_TABLE_PAGE_SIZE,
  pagination,
  sorting,
  className,
}: DataTableProps<TData>) => {
  const { t } = useI18nTranslation();
  const [clientSorting, setClientSorting] = useState<SortingState>([]);

  const sortingState = useMemo<SortingState>(
    () => (sorting ? [{ id: sorting.sortBy, desc: sorting.sortDirection === 'desc' }] : clientSorting),
    [clientSorting, sorting],
  );

  const serverPageCount = pagination ? Math.max(1, Math.ceil(pagination.total / Math.max(1, pagination.pageSize))) : undefined;

  const table = useReactTable({
    data,
    columns,
    state: { sorting: sortingState },
    onSortingChange: setClientSorting,
    manualSorting: sorting !== undefined,
    manualPagination: pagination !== undefined,
    pageCount: serverPageCount,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: sorting ? undefined : getSortedRowModel(),
    getPaginationRowModel: pagination ? undefined : getPaginationRowModel(),
    initialState: pagination ? undefined : { pagination: { pageIndex: 0, pageSize } },
  });

  const rows = table.getRowModel().rows;
  const pageCount = serverPageCount ?? table.getPageCount();
  const page = pagination ? pagination.page : table.getState().pagination.pageIndex + 1;
  const canGoPrevious = pagination ? pagination.page > 1 : table.getCanPreviousPage();
  const canGoNext = pagination ? pagination.page < pageCount : table.getCanNextPage();

  const goToPage = (next: number): void => {
    if (pagination) {
      pagination.onPageChange(next);

      return;
    }

    table.setPageIndex(next - 1);
  };

  return (
    <div className={cn('space-y-3', className)}>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className="w-full border-collapse text-sm">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr
                key={headerGroup.id}
                className="border-b border-border"
              >
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sortDirection = header.column.getIsSorted();
                  const onSort = sorting
                    ? () => sorting.onSortChange(header.column.id, sortDirection === 'asc' ? 'desc' : 'asc')
                    : header.column.getToggleSortingHandler();

                  return (
                    <th
                      key={header.id}
                      scope="col"
                      className="whitespace-nowrap px-4 py-3 text-start text-xs font-medium uppercase tracking-wide text-muted-foreground"
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          type="button"
                          onClick={onSort}
                          className="inline-flex items-center gap-1 transition-colors hover:text-foreground"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {sortDirection === 'asc' ? (
                            <ArrowUp className="size-3" />
                          ) : sortDirection === 'desc' ? (
                            <ArrowDown className="size-3" />
                          ) : (
                            <ChevronsUpDown className="size-3 opacity-50" />
                          )}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {isLoading ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-12 text-center text-muted-foreground"
                >
                  <span className="inline-flex items-center gap-2">
                    <Spinner />
                    {t('table.loading')}
                  </span>
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-12 text-center text-muted-foreground"
                >
                  {emptyMessage ?? t('table.empty')}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={row.id}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  className={cn('border-b border-border last:border-b-0', onRowClick && 'cursor-pointer transition-colors hover:bg-muted')}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-4 py-3 text-start align-middle text-foreground"
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {!isLoading && pageCount > 1 ? (
        <div className="flex items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground">
            {t('table.page', { page, totalPages: pageCount })}
            {pagination ? ` · ${t('table.total', { total: pagination.total })}` : ''}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!canGoPrevious}
              onClick={() => goToPage(page - 1)}
            >
              {t('actions.previous')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!canGoNext}
              onClick={() => goToPage(page + 1)}
            >
              {t('actions.next')}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
